import {
  Timestamp,
  collection,
  connectFirestoreEmulator,
  doc,
  getFirestore,
} from "firebase/firestore"
import type {
  CollectionReference,
  DocumentData,
  DocumentReference,
  DocumentSnapshot,
  Firestore,
} from "firebase/firestore"
import { getFirebaseApp, isFirebaseEmulator } from "./config"
import {
  DEFAULT_SESSION_CONFIG,
  INITIAL_TIMER_STATE,
  ROUND_ORDER,
  ROUND_PHASES,
} from "@/types/session"
import { MOSCOW_BUCKETS } from "@/types/job"
import { NOT_REMOVED, REMOVAL_KINDS } from "@/types/removal"
import type {
  CardId,
  CardPick,
  CardPlacement,
  Customer,
  DeepDiveDetail,
  Facilitator,
  Job,
  MoscowBucket,
  Pain,
  Participant,
  Removable,
  Round,
  Session,
  SessionNotes,
} from "@/types"

export function getDb(): Firestore {
  const db = getFirestore(getFirebaseApp())
  if (isFirebaseEmulator()) connectFirestoreEmulatorOnce(db)
  return db
}

// Dev only. Guarded on globalThis so repeated getDb calls and HMR module reloads
// connect exactly once — connectFirestoreEmulator throws if called twice.
const emulatorScope = globalThis as unknown as { __fsEmulated?: boolean }
function connectFirestoreEmulatorOnce(db: Firestore): void {
  if (emulatorScope.__fsEmulated) return
  emulatorScope.__fsEmulated = true
  connectFirestoreEmulator(db, "127.0.0.1", 8080)
}

/* ---------------------------------------------------------------- references */

/*
 * facilitators/{uid}
 * customers/{customerId}
 * sessions/{sessionId}
 * sessions/{sessionId}/participants/{participantId}
 * sessions/{sessionId}/pains/{painId}
 * sessions/{sessionId}/jobs/{jobId}
 * sessions/{sessionId}/jobs/{jobId}/picks/{participantId}
 * sessions/{sessionId}/private/notes
 */
const COLLECTIONS = {
  facilitators: "facilitators",
  customers: "customers",
  sessions: "sessions",
  participants: "participants",
  pains: "pains",
  jobs: "jobs",
  picks: "picks",
  private: "private",
} as const

/** The one document in `private`: everything under it is facilitator-only. */
const NOTES_DOC = "notes"

export function facilitatorsCollection(): CollectionReference<DocumentData> {
  return collection(getDb(), COLLECTIONS.facilitators)
}

export function facilitatorDoc(uid: string): DocumentReference<DocumentData> {
  return doc(getDb(), COLLECTIONS.facilitators, uid)
}

export function customersCollection(): CollectionReference<DocumentData> {
  return collection(getDb(), COLLECTIONS.customers)
}

export function customerDoc(customerId: string): DocumentReference<DocumentData> {
  return doc(getDb(), COLLECTIONS.customers, customerId)
}

export function sessionsCollection(): CollectionReference<DocumentData> {
  return collection(getDb(), COLLECTIONS.sessions)
}

export function sessionDoc(sessionId: string): DocumentReference<DocumentData> {
  return doc(getDb(), COLLECTIONS.sessions, sessionId)
}

export function participantsCollection(sessionId: string): CollectionReference<DocumentData> {
  return collection(getDb(), COLLECTIONS.sessions, sessionId, COLLECTIONS.participants)
}

export function participantDoc(
  sessionId: string,
  participantId: string
): DocumentReference<DocumentData> {
  return doc(getDb(), COLLECTIONS.sessions, sessionId, COLLECTIONS.participants, participantId)
}

export function painsCollection(sessionId: string): CollectionReference<DocumentData> {
  return collection(getDb(), COLLECTIONS.sessions, sessionId, COLLECTIONS.pains)
}

export function painDoc(sessionId: string, painId: string): DocumentReference<DocumentData> {
  return doc(getDb(), COLLECTIONS.sessions, sessionId, COLLECTIONS.pains, painId)
}

export function jobsCollection(sessionId: string): CollectionReference<DocumentData> {
  return collection(getDb(), COLLECTIONS.sessions, sessionId, COLLECTIONS.jobs)
}

export function jobDoc(sessionId: string, jobId: string): DocumentReference<DocumentData> {
  return doc(getDb(), COLLECTIONS.sessions, sessionId, COLLECTIONS.jobs, jobId)
}

export function picksCollection(
  sessionId: string,
  jobId: string
): CollectionReference<DocumentData> {
  return collection(
    getDb(),
    COLLECTIONS.sessions,
    sessionId,
    COLLECTIONS.jobs,
    jobId,
    COLLECTIONS.picks
  )
}

export function pickDoc(
  sessionId: string,
  jobId: string,
  participantId: string
): DocumentReference<DocumentData> {
  return doc(
    getDb(),
    COLLECTIONS.sessions,
    sessionId,
    COLLECTIONS.jobs,
    jobId,
    COLLECTIONS.picks,
    participantId
  )
}

/**
 * The facilitator's notes. A subcollection document rather than a field on the
 * session, because the session is world-readable and Firestore reads whole
 * documents — see the note at the top of `firestore.rules`.
 */
export function sessionNotesDoc(sessionId: string): DocumentReference<DocumentData> {
  return doc(getDb(), COLLECTIONS.sessions, sessionId, COLLECTIONS.private, NOTES_DOC)
}

/* ------------------------------------------------------------------ mappers */

/**
 * Firestore `Timestamp` values are converted to epoch ms here so that no
 * Firebase type reaches the store or the UI. Returns null while a
 * `serverTimestamp()` write is still pending (Firestore reports it as null
 * in the local echo before the server acknowledges).
 */
function toMillis(value: unknown): number | null {
  if (value instanceof Timestamp) return value.toMillis()
  if (typeof value === "number") return value
  return null
}

function str(value: unknown, fallback = ""): string {
  return typeof value === "string" ? value : fallback
}

function bool(value: unknown, fallback = false): boolean {
  return typeof value === "boolean" ? value : fallback
}

function num(value: unknown, fallback: number): number {
  return typeof value === "number" ? value : fallback
}

/** Drops anything that is not a string, rather than rendering it. */
function strings(value: unknown): string[] {
  if (!Array.isArray(value)) return []
  return (value as unknown[]).filter((entry): entry is string => typeof entry === "string")
}

/**
 * Validates a value against a known set instead of blind-casting it. A stray
 * round written by hand (or by an older build) would otherwise flow into
 * `ROUND_PHASES[round]` as undefined and crash progress derivation.
 */
function oneOf<T extends string>(value: unknown, allowed: readonly T[], fallback: T): T {
  return typeof value === "string" && (allowed as readonly string[]).includes(value)
    ? (value as T)
    : fallback
}

const SESSION_STATUSES = ["pending", "active", "completed"] as const
const OVERLAY_KINDS = ["ready", "deck", "board", "dive", "wrap", "jobPick", "jobGate"] as const
const TIMER_STATUSES = ["idle", "running", "paused"] as const

/*
 * Names this app used before the flow was modelled around jobs. Kept so a
 * session written by an older build still opens where it was left, rather than
 * failing validation and silently resetting to the lobby — which, mid-workshop,
 * looks to the room like the session was wiped.
 *
 * The phases line up unchanged: `frame/draft` is `job/draft`, `draft/pick` is
 * `cards/pick`, and so on, so only the round name needs translating.
 */
const LEGACY_ROUNDS: Record<string, Round> = {
  frame: "job",
  draft: "cards",
  place: "priorities",
  build: "wrap",
}

const LEGACY_STATUSES: Record<string, Session["status"]> = {
  // A session that had not been started yet.
  draft: "pending",
  // Pausing is the timer's job now; the session itself is still running.
  paused: "active",
}

function toRound(value: unknown): Round {
  const named = typeof value === "string" ? (LEGACY_ROUNDS[value] ?? value) : value
  return oneOf(named, ROUND_ORDER, "lobby")
}

function toStatus(value: unknown): Session["status"] {
  const named = typeof value === "string" ? (LEGACY_STATUSES[value] ?? value) : value
  return oneOf(named, SESSION_STATUSES, "pending")
}

/**
 * The removal fields, for any document that can be archived or deleted.
 *
 * ABSENT and UNRECOGNISED are not the same answer, and reading them as one is
 * what this used to get wrong.
 *
 * Absent is present: every document written before removal existed has no
 * `removedAs` at all, and `firestore.rules` `isRemoved` agrees — it tests for
 * the field before it tests the value.
 *
 * An unrecognised value is REMOVED, because the rules say it is: anything
 * non-null there makes `isRemoved` true, so the session is not `isLive` and
 * every write beneath it is refused. Reading it as present put the client on the
 * other side of that — a room drawn as live and writable whose every write came
 * back denied, with no screen able to say why. Failing closed costs a row that
 * looks removed; failing open costs a room that does not work.
 *
 * `deleted` rather than `archived` is the closed end of the two: it offers no
 * Restore. Nothing the app can write lands here — `isRemovalStamp` pins the
 * value to the enum — so this is reachable only by an Admin SDK write or a
 * future kind this build predates, and both want the cautious reading.
 */
function toRemovable(data: DocumentData): Removable {
  if (data.removedAs === undefined || data.removedAs === null) return NOT_REMOVED
  const kind = REMOVAL_KINDS.find((entry) => entry === data.removedAs) ?? "deleted"
  return {
    removedAt: toMillis(data.removedAt),
    removedAs: kind,
    removedBy: typeof data.removedBy === "string" ? data.removedBy : null,
  }
}

export function toFacilitator(snapshot: DocumentSnapshot<DocumentData>): Facilitator | null {
  const data = snapshot.data()
  if (!data) return null
  return {
    id: snapshot.id,
    email: str(data.email),
    displayName: str(data.displayName),
    photoUrl: str(data.photoUrl),
    lastSeenAt: toMillis(data.lastSeenAt),
  }
}

export function toCustomer(snapshot: DocumentSnapshot<DocumentData>): Customer | null {
  const data = snapshot.data()
  if (!data) return null
  return {
    id: snapshot.id,
    name: str(data.name),
    sector: str(data.sector),
    size: str(data.size),
    about: str(data.about),
    facilitatorIds: strings(data.facilitatorIds),
    ...toRemovable(data),
    createdAt: toMillis(data.createdAt),
    updatedAt: toMillis(data.updatedAt),
  }
}

/** Keeps only durations for rounds that exist, translating the old names. */
function toRoundDurations(value: unknown): Session["config"]["roundDurations"] {
  const durations = { ...DEFAULT_SESSION_CONFIG.roundDurations }
  if (!value || typeof value !== "object") return durations

  for (const [key, minutes] of Object.entries(value as Record<string, unknown>)) {
    if (typeof minutes !== "number") continue
    const round = LEGACY_ROUNDS[key] ?? key
    if ((ROUND_ORDER as readonly string[]).includes(round)) durations[round as Round] = minutes
  }
  return durations
}

export function toSession(snapshot: DocumentSnapshot<DocumentData>): Session | null {
  const data = snapshot.data()
  if (!data) return null

  const config = (data.config ?? {}) as Record<string, unknown>
  const timer = (data.timer ?? {}) as Partial<Session["timer"]>
  // Validate round first: phase is only meaningful relative to its round.
  const round = toRound(data.round)

  // An unrecognised overlay kind is treated as "no overlay" rather than
  // trapping the room behind a curtain it cannot dismiss.
  const rawOverlay = data.overlay as { kind?: unknown; selection?: unknown } | null | undefined
  const overlayKind =
    rawOverlay && typeof rawOverlay === "object"
      ? OVERLAY_KINDS.find((k) => k === rawOverlay.kind)
      : undefined

  return {
    id: snapshot.id,
    customerId: str(data.customerId),
    customerName: str(data.customerName),
    facilitatorIds: strings(data.facilitatorIds),
    name: str(data.name),
    status: toStatus(data.status),
    round,
    phase: oneOf(data.phase, ROUND_PHASES[round], ROUND_PHASES[round][0]),
    currentJobId: typeof data.currentJobId === "string" ? data.currentJobId : null,
    overlay: overlayKind
      ? {
          kind: overlayKind,
          selection:
            typeof rawOverlay?.selection === "string" ? (rawOverlay.selection as string) : null,
        }
      : null,
    knownPains: strings(data.knownPains),
    config: {
      families: Array.isArray(config.families)
        ? (config.families as unknown[]).filter((n): n is number => typeof n === "number")
        : DEFAULT_SESSION_CONFIG.families,
      painsPerParticipant: num(
        config.painsPerParticipant,
        DEFAULT_SESSION_CONFIG.painsPerParticipant
      ),
      votesPerParticipant: num(
        config.votesPerParticipant,
        DEFAULT_SESSION_CONFIG.votesPerParticipant
      ),
      handCap: num(config.handCap, DEFAULT_SESSION_CONFIG.handCap),
      // `proveCap` was the same limit under the fit-board's name.
      mustHaveCap: num(config.mustHaveCap ?? config.proveCap, DEFAULT_SESSION_CONFIG.mustHaveCap),
      painsEnabled: bool(config.painsEnabled, DEFAULT_SESSION_CONFIG.painsEnabled),
      userStoryEnabled: bool(config.userStoryEnabled, DEFAULT_SESSION_CONFIG.userStoryEnabled),
      deepDiveEnabled: bool(config.deepDiveEnabled, DEFAULT_SESSION_CONFIG.deepDiveEnabled),
      roundDurations: toRoundDurations(config.roundDurations),
    },
    timer: {
      status: oneOf(timer.status, TIMER_STATUSES, INITIAL_TIMER_STATE.status),
      startedAt: toMillis(timer.startedAt),
      durationMs: num(timer.durationMs, INITIAL_TIMER_STATE.durationMs),
      elapsedMs: num(timer.elapsedMs, INITIAL_TIMER_STATE.elapsedMs),
    },
    startedAt: toMillis(data.startedAt),
    completedAt: toMillis(data.completedAt),
    pausedAt: toMillis(data.pausedAt),
    expiresAt: toMillis(data.expiresAt),
    ...toRemovable(data),
    createdAt: toMillis(data.createdAt),
    updatedAt: toMillis(data.updatedAt),
  }
}

/**
 * Only the rounds that actually carry text. A note is written under a round key
 * and read back the same way, so anything else on the document — a key from an
 * older flow, a value someone put there by hand — is dropped rather than typed
 * into existence.
 */
export function toSessionNotes(snapshot: DocumentSnapshot<DocumentData>): SessionNotes {
  const data = snapshot.data()
  if (!data) return {}

  const notes: SessionNotes = {}
  for (const round of ROUND_ORDER) {
    if (typeof data[round] === "string") notes[round] = data[round] as string
  }
  return notes
}

export function toParticipant(snapshot: DocumentSnapshot<DocumentData>): Participant | null {
  const data = snapshot.data()
  if (!data) return null
  return {
    id: snapshot.id,
    name: str(data.name),
    jobRole: str(data.jobRole),
    ready: bool(data.ready),
    donePhase: typeof data.donePhase === "string" ? data.donePhase : null,
    joinedAt: toMillis(data.joinedAt),
  }
}

export function toPain(snapshot: DocumentSnapshot<DocumentData>): Pain | null {
  const data = snapshot.data()
  if (!data) return null
  return {
    id: snapshot.id,
    text: str(data.text),
    authorId: typeof data.authorId === "string" ? data.authorId : null,
    authorName: str(data.authorName),
    source: data.source === "known" ? "known" : "room",
    voterIds: strings(data.voterIds),
    createdAt: toMillis(data.createdAt),
    updatedAt: toMillis(data.updatedAt),
  }
}

/**
 * Both card maps are rebuilt entry by entry rather than cast.
 *
 * They are keyed by card id, and a key is whatever was last written — including
 * by a build that spelled a bucket differently. An unreadable entry is dropped
 * here, where it is one missing card, instead of reaching the board as a card
 * filed under a quadrant that does not exist.
 */
function toPlacements(value: unknown): Record<CardId, CardPlacement> {
  const out: Record<CardId, CardPlacement> = {}
  if (!value || typeof value !== "object") return out

  for (const [cardId, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!raw || typeof raw !== "object") continue
    const entry = raw as { bucket?: unknown; note?: unknown }
    const bucket = MOSCOW_BUCKETS.find((b) => b === entry.bucket)
    if (!bucket) continue
    out[cardId] = { bucket: bucket as MoscowBucket, note: str(entry.note) }
  }
  return out
}

function toDeepDive(value: unknown): Record<CardId, DeepDiveDetail> {
  const out: Record<CardId, DeepDiveDetail> = {}
  if (!value || typeof value !== "object") return out

  for (const [cardId, raw] of Object.entries(value as Record<string, unknown>)) {
    if (!raw || typeof raw !== "object") continue
    const entry = raw as Partial<Record<keyof DeepDiveDetail, unknown>>
    out[cardId] = {
      whoseJob: str(entry.whoseJob),
      painToday: str(entry.painToday),
      metric: str(entry.metric),
      whereItLives: str(entry.whereItLives),
    }
  }
  return out
}

export function toJob(snapshot: DocumentSnapshot<DocumentData>): Job | null {
  const data = snapshot.data()
  if (!data) return null
  return {
    id: snapshot.id,
    painIds: strings(data.painIds),
    who: str(data.who),
    what: str(data.what),
    why: str(data.why),
    cardIds: strings(data.cardIds),
    // Absent on jobs written before seeding existed. Treating those as already
    // seeded is the safe reading: their shortlist is whatever the facilitator
    // built by hand, and re-seeding it would add cards they never kept.
    shortlistSeeded: data.shortlistSeeded !== false,
    placements: toPlacements(data.placements),
    deepDive: toDeepDive(data.deepDive),
    deepDiveCardId: typeof data.deepDiveCardId === "string" ? data.deepDiveCardId : null,
    createdAt: toMillis(data.createdAt),
    completedAt: toMillis(data.completedAt),
  }
}

export function toCardPick(snapshot: DocumentSnapshot<DocumentData>): CardPick | null {
  const data = snapshot.data()
  if (!data) return null
  return {
    id: snapshot.id,
    cardIds: strings(data.cardIds),
    updatedAt: toMillis(data.updatedAt),
  }
}
